import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button } from '@kss/ui';
import { ArrowLeft, Printer, RefreshCw } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { companyService, productService, type Company, type Product } from '@kss/api';
import { encodeId } from '../../utils/idCodec';
import { toAbsoluteImageUrl } from '../../utils/persistImage';
import { isQrContrastSafe, useQrColor } from '../../utils/qrColor';

/**
 * BulkQrPrint — A4 sheet of QR labels for a bulk-selected set of
 * products. Zero server state: everything is computed at render time
 * from the router-state `productIds`, the fresh product fetch, and the
 * tenant's Company row.
 *
 * Layout:
 *   210mm × 297mm A4 page, 10mm outer margin, 4 columns × 6 rows =
 *   24 labels per page. When the selection exceeds 24 the print sheet
 *   flows into as many pages as needed; the CSS `page-break-after`
 *   rule inserts a break between them.
 *
 * Each label carries:
 *   - Company logo (or company name text if no logo set).
 *   - Product name (bold, truncated).
 *   - SKU (mono, small).
 *   - QR encoding `${origin}/shop/${encodeId(productId)}` — matches
 *     the storefront's `/shop/:id` route (see `apps/web/src/App.tsx`).
 *   - Tiny storefront URL for human legibility.
 *
 * The `@media print` rule hides the panel chrome + toolbar and reveals
 * only the sheet, so admins can print onto plain paper or 24-up
 * adhesive label sheets.
 *
 * Product ids arrive via router state (fast path — no re-encode) with
 * a `?ids=` query-string fallback so a hard-refresh of the sheet URL
 * still resolves the same selection.
 */

// Company fetch cache — mirrors ReportShell.tsx so we don't refetch on
// every navigation to the sheet. Module-scoped so the singleton
// survives HMR within a session.
let _companyCache: Company | null = null;
let _companyPromise: Promise<Company | null> | null = null;
async function loadCompany(): Promise<Company | null> {
  if (_companyCache) return _companyCache;
  if (_companyPromise) return _companyPromise;
  _companyPromise = (async () => {
    try {
      const rows = await companyService.all();
      const list = Array.isArray(rows) ? rows : [];
      const first = list.find((c) => c.isActive !== false) ?? list[0] ?? null;
      _companyCache = first;
      return first;
    } catch {
      return null;
    } finally {
      _companyPromise = null;
    }
  })();
  return _companyPromise;
}

/** 4×6 label grid → 24 QR codes per A4 sheet. */
const LABELS_PER_PAGE = 24;

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

interface LabelRow {
  productId: string;
  name: string;
  sku: string;
  url: string;
}

export default function BulkQrPrint() {
  const location = useLocation();
  const navigate = useNavigate();

  const productIds = useMemo<string[]>(() => {
    // Router state is the primary channel — populated by AllProducts'
    // "Print QR" toolbar action. Fall back to `?ids=comma,list` so a
    // deep-link / refresh still resolves.
    const state = location.state as { productIds?: string[] } | null;
    if (state?.productIds && Array.isArray(state.productIds)) {
      return state.productIds.filter((id): id is string => typeof id === 'string' && !!id);
    }
    const query = new URLSearchParams(location.search);
    const raw = query.get('ids') ?? '';
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }, [location.state, location.search]);

  const [products, setProducts] = useState<Product[]>([]);
  const [company, setCompany] = useState<Company | null>(_companyCache);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<string | null>(null);
  errorRef.current = error;

  useEffect(() => {
    if (productIds.length === 0) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [rows, comp] = await Promise.all([
          Promise.all(
            productIds.map((id) =>
              productService.get(id).catch(() => null),
            ),
          ),
          loadCompany(),
        ]);
        if (cancelled) return;
        const filtered = rows.filter((p): p is Product => !!p);
        setProducts(filtered);
        setCompany(comp);
        if (filtered.length < productIds.length) {
          setError(
            `${productIds.length - filtered.length} product(s) failed to load — the sheet only shows those we could fetch.`,
          );
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productIds]);

  const labels = useMemo<LabelRow[]>(() => {
    const origin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    return products
      .filter((p) => !!p.productId)
      .map((p) => ({
        productId: p.productId as string,
        name: p.name ?? '',
        sku: p.sku ?? '',
        url: `${origin}/shop/${encodeId(p.productId as string)}`,
      }));
  }, [products]);

  const pages = useMemo(() => chunk(labels, LABELS_PER_PAGE), [labels]);
  /* Read-only here — the colour is chosen on the product QR card and
     shared through the same preference, so a sheet printed straight
     after picking a colour matches what the admin previewed. */
  const { color: qrFgColor } = useQrColor();
  const contrastSafe = isQrContrastSafe(qrFgColor);
  const logoUrl = company?.logoUrl ? toAbsoluteImageUrl(company.logoUrl) : '';
  const companyName = company?.legalName || company?.name || '';

  const handlePrint = () => {
    // Delay a frame so any pending layout / image decode finishes
    // before the browser snapshots for print. Otherwise QR canvases
    // occasionally render blank in the first print pass.
    requestAnimationFrame(() => window.print());
  };

  return (
    <div className="qr-print-page -m-6 p-[3px]">
      {/* Chrome — hidden from print output via the CSS at the bottom. */}
      <div className="qr-print-actions mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/products')}>
            <ArrowLeft className="h-3.5 w-3.5" /> Back to products
          </Button>
          <span className="text-xs text-content-muted">
            {loading
              ? 'Loading products…'
              : `${labels.length} label${labels.length === 1 ? '' : 's'} · ${pages.length} A4 page${pages.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {loading && <RefreshCw className="h-3.5 w-3.5 animate-spin text-content-muted" />}
          <Button size="sm" onClick={handlePrint} disabled={loading || labels.length === 0}>
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="warning" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Sits inside `.qr-print-actions`-adjacent chrome, so the print
          CSS below keeps it off the sheet itself. */}
      {!contrastSafe && labels.length > 0 && (
        <Alert variant="warning" className="qr-print-actions mb-3">
          The selected QR colour is light against white and may not scan reliably once printed.
          Change it on any product's QR card, or switch back to black before a large run.
        </Alert>
      )}

      {!loading && labels.length === 0 && (
        <Alert variant="info">
          Nothing to print — select one or more products from the grid and click "Print QR".
        </Alert>
      )}

      {/* Print sheet — one <section class="qr-sheet"> per A4 page. */}
      <div className="qr-print-sheet">
        {pages.map((pageLabels, pi) => (
          <section key={`page-${pi}`} className="qr-sheet">
            <div className="qr-sheet-grid">
              {pageLabels.map((lbl) => (
                <div key={lbl.productId} className="qr-label">
                  <div className="qr-label-header">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={companyName || 'Company logo'}
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                        className="qr-label-logo"
                      />
                    ) : (
                      <span className="qr-label-companyname" title={companyName}>
                        {companyName || 'Company'}
                      </span>
                    )}
                  </div>
                  <div className="qr-label-qrwrap">
                    <QRCodeCanvas
                      value={lbl.url}
                      size={100}
                      level="M"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor={qrFgColor}
                    />
                  </div>
                  <div className="qr-label-name" title={lbl.name}>
                    {lbl.name || '—'}
                  </div>
                  {lbl.sku && <div className="qr-label-sku">SKU {lbl.sku}</div>}
                  <div className="qr-label-url" title={lbl.url}>
                    {lbl.url}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <style>{`
        .qr-print-sheet { display: flex; flex-direction: column; align-items: center; gap: 12px; }
        .qr-sheet {
          width: 210mm; min-height: 297mm; padding: 10mm; box-sizing: border-box;
          background: #fff; color: #111; border: 1px solid #d8caf2;
          border-radius: 6px;
        }
        .qr-sheet + .qr-sheet { margin-top: 12px; }
        .qr-sheet-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-auto-rows: 1fr;
          gap: 4mm;
        }
        .qr-label {
          display: flex; flex-direction: column; align-items: center;
          border: 1px solid #e5e7eb; border-radius: 4px;
          padding: 3mm 2mm; gap: 1mm;
          font-family: system-ui, -apple-system, sans-serif;
          background: #fff; color: #111;
          box-sizing: border-box; overflow: hidden;
          break-inside: avoid;
        }
        .qr-label-header {
          width: 100%; height: 8mm;
          display: flex; align-items: center; justify-content: flex-start;
        }
        .qr-label-logo { max-height: 8mm; max-width: 60%; object-fit: contain; }
        .qr-label-companyname {
          font-size: 8px; font-weight: 600; color: #555;
          max-width: 100%;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .qr-label-qrwrap {
          padding: 1mm; background: #fff;
          display: flex; align-items: center; justify-content: center;
        }
        .qr-label-qrwrap canvas { width: 100px; height: 100px; }
        .qr-label-name {
          font-size: 12px; font-weight: 700; text-align: center;
          width: 100%; overflow: hidden; text-overflow: ellipsis;
          white-space: nowrap;
        }
        .qr-label-sku {
          font-family: ui-monospace, Menlo, Consolas, monospace;
          font-size: 10px; color: #333;
        }
        .qr-label-url {
          font-family: ui-monospace, Menlo, Consolas, monospace;
          font-size: 6px; color: #888;
          width: 100%; overflow: hidden; text-overflow: ellipsis;
          white-space: nowrap; text-align: center;
        }

        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: #fff !important; }
          .qr-print-actions { display: none !important; }
          aside, header, footer, nav { display: none !important; }
          /* Hide any layout chrome from PanelLayout — but keep the
             print sheet visible. */
          body * { visibility: hidden; }
          .qr-print-sheet, .qr-print-sheet * { visibility: visible; }
          .qr-print-page { position: absolute; inset: 0; margin: 0 !important; padding: 0 !important; }
          .qr-print-sheet { gap: 0; }
          .qr-sheet {
            border: 0 !important; border-radius: 0 !important;
            width: 210mm; height: 297mm;
            padding: 10mm; margin: 0;
            page-break-after: always;
          }
          .qr-sheet:last-child { page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}
